/**
 * Eligibility for finalizing a LIVE streaming session, shared by Dataset Info and Day Info.
 *
 * Finalization fills in what the live path could not produce (map, races, analysis grades, report
 * pages) on the SAME dataset_id, without an upload — see server_python/scripts/gp50/finalize.py.
 *
 * Every rule here is ALSO enforced by finalize.py, deliberately. This copy exists so the UI can
 * explain itself and so a day-level run can skip an ineligible boat without a round trip; it is not
 * the security boundary. When the two disagree, the script wins.
 */

/** Why a dataset cannot be finalized. `ok` carries no reason. */
export type FinalizeIneligibleReason =
  | "not-live-dataset"
  | "already-finalized"
  | "streaming-active";

export type FinalizeEligibility =
  | { ok: true }
  | { ok: false; reason: FinalizeIneligibleReason; message: string };

/** The script name the queue runs. */
export const FINALIZE_LIVE_SCRIPT = "finalize.py";

type Tags = Record<string, unknown> | null | undefined;

/**
 * Coerce whatever a row carries in `tags` into an object.
 *
 * The list endpoints return the jsonb blob, which node-postgres gives us as an object, but the
 * single-dataset endpoint hands back a JSON string. Accepting both means one predicate serves every
 * caller instead of each one remembering which shape it has.
 */
export function coerceTags(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value === "string" && value.trim() !== "") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }
  return null;
}

function readBoolTag(tags: Tags, key: string): boolean {
  const value = tags?.[key];
  if (typeof value === "boolean") { return value; }
  if (typeof value === "string") { return value.trim().toLowerCase() === "true"; }
  return false;
}

/** True when the dataset row was created by the live path (`tags.isLive`). */
export function isLiveDatasetTags(tags: Tags): boolean {
  return readBoolTag(tags, "isLive");
}

/**
 * True once finalize.py has stamped the dataset.
 *
 * `isLive` deliberately STAYS true after finalizing — a later import matches on it to claim and
 * purge the day's live parquet — so `finalizedAt` is the only marker that the work has been done.
 */
export function isFinalizedTags(tags: Tags): boolean {
  const value = tags?.finalizedAt;
  return typeof value === "string" && value.trim() !== "";
}

export interface FinalizeQualifyOptions {
  /** Is a streaming session running right now (any source)? */
  streamingActive?: boolean;
  /** Is this dataset today's? Only today's can be the one being streamed into. */
  isToday?: boolean;
}

/**
 * Whether this dataset can be finalized now.
 *
 * The streaming check is scoped to today: the stream status is global, so without that scope a live
 * session would block finalizing every past day too.
 */
export function qualifyLiveDatasetForFinalize(
  tags: Tags,
  options: FinalizeQualifyOptions = {}
): FinalizeEligibility {
  if (!isLiveDatasetTags(tags)) {
    return { ok: false, reason: "not-live-dataset", message: "not a live streaming session" };
  }
  if (isFinalizedTags(tags)) {
    return {
      ok: false,
      reason: "already-finalized",
      message: `already finalized at ${String(tags?.finalizedAt)}`,
    };
  }
  if (options.streamingActive === true && options.isToday === true) {
    return {
      ok: false,
      reason: "streaming-active",
      message: "a streaming session is running — stop it first",
    };
  }
  return { ok: true };
}

/**
 * Does this dataset list row still need finalizing?
 *
 * Reads the row's `tags` and nothing else. Deliberately NOT `report_name`, `visible` or
 * `isNewDataset`:
 *   - `visible` is 0 on a stopped live session (hideLiveDatasets sets it when streaming ends —
 *     exactly the state you finalize from), so it says nothing about eligibility;
 *   - `report_name` stays 'LIVE' forever, even after finalizing, so it cannot tell done from
 *     not-done.
 * Both were tried and both put the button where live rows never render.
 *
 * A day-aggregated row (the ALL view) has no tags — fleet_datasets groups over boats — so it
 * carries `unfinalized_live_count` instead. The streaming gate applies to it exactly as it does to
 * a per-boat row: a day still being streamed into is not finalizable, whatever the count says.
 *
 * `options` is how a caller supplies the one fact the row cannot carry: whether a session is
 * streaming right now. Omitting it keeps the tags-only answer — right for "should this row exist at
 * all", wrong for "should the button be pressable".
 */
export function rowNeedsFinalize(
  row: Record<string, unknown> | null | undefined,
  options: FinalizeQualifyOptions = {}
): boolean {
  if (!row) { return false; }

  const streamingNow = options.streamingActive === true && options.isToday === true;

  const dayCount = row.unfinalized_live_count;
  if (dayCount !== undefined && dayCount !== null) {
    return !streamingNow && Number(dayCount) > 0;
  }

  return qualifyLiveDatasetForFinalize(coerceTags(row.tags), options).ok;
}

function hasDeletableDatasetId(id: unknown): boolean {
  if (typeof id === "number") { return Number.isFinite(id) && id > 0; }
  if (typeof id === "string" && id.trim() !== "") {
    const parsed = Number(id);
    return Number.isFinite(parsed) && parsed > 0;
  }
  return false;
}

/**
 * May an admin delete this row?
 *
 * Finalize stamps `finalizedAt` only after the whole run succeeds, and it never renames the row
 * away from LIVE. A run that stops short therefore stays on the live row, which is the one place
 * the datasets table used to omit Delete — so a half-finished session could not be removed.
 * The same rule covers a stopped session that was never finalized. A row a session is writing
 * into right now is not deletable. A day-aggregated row has no dataset id, so it is not either.
 */
export function rowAllowsDeletion(
  row: { dataset_id?: unknown; report_name?: unknown } | null | undefined,
  options: FinalizeQualifyOptions = {}
): boolean {
  if (!row || !hasDeletableDatasetId(row.dataset_id)) { return false; }
  const streamingNow = options.streamingActive === true && options.isToday === true;
  const reportName = String(row.report_name ?? "").trim().toUpperCase();
  if (streamingNow && reportName === "LIVE") { return false; }
  return true;
}

/**
 * The confirm text. States the cost of gapfilling, because the resulting numbers are NOT
 * comparable with an uploaded day's and that is not visible anywhere on the report afterwards.
 */
export function confirmFinalizeMessage(subject: string): string {
  return [
    `Finalize ${subject}?`,
    "",
    "This builds the map, races and report pages from the streamed data, in place.",
    "",
    "It keeps the live maneuvers rather than recomputing them, so their windows differ from an",
    "uploaded day's and the two are not directly comparable. PHASE, PERIOD, TEST and BIN 5",
    "aggregates stay missing. The dataset keeps its LIVE name until the real logs are uploaded.",
    "",
    "Do not start or stop a streaming session while this runs.",
  ].join("\n");
}
